// import cv from '@techstark/opencv-js';
import type { ContourProps } from './contour-definitions';
import type { Point, PointArray } from './types/contour-definition-array-type';

import { AreaContourBase } from './area-contour-base';
import { ContourBase, ContourType } from './contour-definitions';
import { processPoints } from './contour-processing/contourProcessing';
import { getClosestPointIdx } from './contour-processing/getClosestPointIdx';
import { getPolygonArea } from './contour-processing/getPolygonArea';

class PointNode {
  nodePoint: Point;

  next: PointNode | null = null;

  previous: PointNode | null = null;

  constructor(nodePoint: Point = { x: 0, y: 0 }) {
    this.nodePoint = nodePoint;
  }
}

export class PolygonClosedContour extends AreaContourBase {
  constructor(
    props: Partial<ContourProps> & Pick<ContourProps, 'type' | 'label'>,
    polygon: PointArray = [],
  ) {
    super(props, polygon);

    if (props.type !== ContourType.PolygonClosedContour) {
      throw new Error('PolygonClosedContour - Invalid contour type');
    }
  }

  protected postSetPolygon(): void {
    //
  }

  protected postSetPolygonScaled(): void {
    // this._polygon = removeLoops(this._polygon);
  }

  /**
   * modifyContour
   * @param polygon PointArray to modify contour given in image coordinates
   * modifies to closed contour
   */
  public modifyContour(polygon: PointArray) {
    let modifyPointArray = processPoints(
      polygon,
      false,
      this.getImageWidth(),
      this.getImageHeight(),
    );

    if (modifyPointArray.length < 3) {
      return;
    }

    for (let i = 0; i < modifyPointArray.length; i++) {
      modifyPointArray[i].x *= ContourBase.subMatrix;
      modifyPointArray[i].y *= ContourBase.subMatrix;
    }

    // remove duplicate last point
    const tmpPointArray = this._polygon.slice(0, -1);

    if (
      modifyPointArray[0].x
      === modifyPointArray[modifyPointArray.length - 1].x
      && modifyPointArray[0].y === modifyPointArray[modifyPointArray.length - 1].y
    ) {
      console.log('modifyContour - remove duplicate last point');
      modifyPointArray.pop();
    }

    const insertionPointIndexOne = getClosestPointIdx(
      modifyPointArray[0],
      tmpPointArray,
    );
    const insertionPointIndexTwo = getClosestPointIdx(
      modifyPointArray[modifyPointArray.length - 1],
      tmpPointArray,
    );

    // console.log('insertionPointIndexOne', insertionPointIndexOne);
    // console.log('insertionPointIndexTwo', insertionPointIndexTwo);
    // console.log('tmpPointArray', tmpPointArray);
    // console.log('modifyPointArray', modifyPointArray);

    if (insertionPointIndexOne === insertionPointIndexTwo) {
      return;
    }

    const modifiedPointArrayOne = this.getLinkedListInsertionPolygon(
      insertionPointIndexOne,
      insertionPointIndexTwo,
      tmpPointArray,
      modifyPointArray,
    );

    // Reverse insertion array
    modifyPointArray = [...modifyPointArray].reverse();

    const modifiedPointArrayTwo = this.getLinkedListInsertionPolygon(
      insertionPointIndexTwo,
      insertionPointIndexOne,
      tmpPointArray,
      modifyPointArray,
    );

    const areaModifiedOne = Math.abs(getPolygonArea(modifiedPointArrayOne));
    const areaModifiedTwo = Math.abs(getPolygonArea(modifiedPointArrayTwo));

    let finalPointArray: PointArray = [];
    if (areaModifiedOne >= areaModifiedTwo) {
      finalPointArray = modifiedPointArrayOne;
    }
    else {
      finalPointArray = modifiedPointArrayTwo;
    }

    for (let i = 0; i < finalPointArray.length; i++) {
      finalPointArray[i].x /= ContourBase.subMatrix;
      finalPointArray[i].y /= ContourBase.subMatrix;
    }
    finalPointArray.push(finalPointArray[0]);

    finalPointArray = processPoints(
      finalPointArray,
      true,
      this.getImageWidth(),
      this.getImageHeight(),
    );

    // finalPointArray = interpolateGridPoints2D(finalPointArray, 1);
    // finalPointArray = snapPolyLineToSubPixelGrid(finalPointArray);
    // // finalPointArray = removeLoops(finalPointArray);

    for (let i = 0; i < finalPointArray.length; i++) {
      finalPointArray[i].x *= ContourBase.subMatrix;
      finalPointArray[i].y *= ContourBase.subMatrix;
    }

    this.setPolygon(finalPointArray);
  }

  private removeDuplicatesUsingMap(points: PointArray): PointArray {
    const pointMap = new Map<string, Point>();

    points.forEach((point) => {
      const key = `${point.x},${point.y}`;
      if (!pointMap.has(key)) {
        pointMap.set(key, point); // Map stores unique keys
      }
      else {
        console.log('removeDuplicatesUsingMap - duplicate point', point);
      }
    });

    return Array.from(pointMap.values());
  }

  // private getLinkedListInsertionPolygon(
  //   insertionPointIndexOne: number,
  //   insertionPointIndexTwo: number,
  //   changePointArray: PointArray,
  //   modifyPointArray: PointArray): PointArray {

  //     console.log('getLinkedListInsertionPolygon');

  //   const nodeArray: PointNode[] = changePointArray.map((point) => ({
  //     nodePoint: point,
  //     next: null,
  //     previous: null,
  //   }));

  //   let insertionNodeOne = new PointNode();
  //   let insertionNodeTwo = new PointNode();

  //   // create and get closest nodes
  //   for (let i = 0; i < nodeArray.length; i++) {
  //     const currentNode = nodeArray[i];
  //     const nextNode = nodeArray[(i + 1) % nodeArray.length]; // Wraps around to first node
  //     const previousNode = nodeArray[(i - 1 + nodeArray.length) % nodeArray.length]; // Wraps around to last node

  //     currentNode.next = nextNode;
  //     currentNode.previous = previousNode;

  //     if (i === insertionPointIndexOne) {
  //       insertionNodeOne = currentNode;
  //     }
  //     if (i === insertionPointIndexTwo) {
  //       insertionNodeTwo = currentNode;
  //     }
  //   }

  //   // create insertion linked list
  //   const nodeArrayInsert: PointNode[] = modifyPointArray.map((point) => ({
  //     nodePoint: point,
  //     next: null,
  //     previous: null,
  //   }));

  //   for (let i = 0; i < nodeArrayInsert.length; i++) {

  //     if (i === 0) {
  //       nodeArrayInsert[i].previous = insertionNodeOne;
  //       insertionNodeOne.next = nodeArrayInsert[i];
  //       nodeArrayInsert[i].next = null;
  //     }

  //     if (i > 0 && i < nodeArrayInsert.length - 1) {
  //       const currentNode = nodeArrayInsert[i];
  //       const nextNode = nodeArrayInsert[(i + 1)];
  //       const previousNode = nodeArrayInsert[(i - 1 + nodeArrayInsert.length)];

  //       currentNode.next = nextNode;
  //       currentNode.previous = previousNode;
  //     }

  //     if (i === nodeArrayInsert.length - 1) {
  //       nodeArrayInsert[i].previous = nodeArrayInsert[i - 1];
  //       nodeArrayInsert[i].previous!.next = nodeArrayInsert[i];
  //       nodeArrayInsert[i].next = insertionNodeTwo;
  //       insertionNodeTwo.previous = nodeArrayInsert[i];
  //     }
  //   }

  //   const modifiedPointArray = [];
  //   modifiedPointArray.push(insertionNodeOne.nodePoint);

  //   let tmpNode = insertionNodeOne.next;
  //   while (tmpNode !== insertionNodeOne) {
  //     modifiedPointArray.push(tmpNode!.nodePoint);

  //     if (tmpNode!.next !== null) {
  //       tmpNode = tmpNode!.next;
  //     } else {
  //       tmpNode = insertionNodeOne;
  //     }
  //   }

  //   //  close
  //   modifiedPointArray.push(insertionNodeOne.nodePoint);

  //   return modifiedPointArray;
  // }

  private getLinkedListInsertionPolygon(
    insertionPointIndexOne: number,
    insertionPointIndexTwo: number,
    changePointArray: PointArray,
    modifyPointArray: PointArray,
  ): PointArray {
    console.log('getLinkedListInsertionPolygon');

    // Create the linked list from changePointArray
    const nodeArray: PointNode[] = changePointArray.map(
      point => new PointNode(point),
    );

    // Link the nodes in a circular fashion
    nodeArray.forEach((currentNode, i) => {
      currentNode.next = nodeArray[(i + 1) % nodeArray.length]; // Wrap to the first node

      currentNode.previous
        = nodeArray[(i - 1 + nodeArray.length) % nodeArray.length]; // Wrap to the last node
    });

    // Get insertion nodes
    const insertionNodeOne = nodeArray[insertionPointIndexOne];
    const insertionNodeTwo = nodeArray[insertionPointIndexTwo];

    // Create the insertion linked list from modifyPointArray
    const nodeArrayInsert: PointNode[] = modifyPointArray.map(
      point => new PointNode(point),
    );

    // Link the insertion list
    nodeArrayInsert.forEach((currentNode, i) => {
      if (i === 0) {
        // First node in the insertion list

        currentNode.previous = insertionNodeOne;
        insertionNodeOne.next = currentNode;
      }
      else {
        currentNode.previous = nodeArrayInsert[i - 1];
        nodeArrayInsert[i - 1].next = currentNode;
      }

      if (i === nodeArrayInsert.length - 1) {
        // Last node in the insertion list

        currentNode.next = insertionNodeTwo;
        insertionNodeTwo.previous = currentNode;
      }
    });

    // Traverse and build the modified point array starting from insertionNodeOne
    const modifiedPointArray: PointArray = [];
    let tmpNode: PointNode | null = insertionNodeOne;

    do {
      modifiedPointArray.push(tmpNode.nodePoint);
      tmpNode = tmpNode.next;
    } while (tmpNode && tmpNode !== insertionNodeOne);

    // Close the polygon
    // modifiedPointArray.push(insertionNodeOne.nodePoint);

    return modifiedPointArray;
  }

  // ─── Public Method for PolygonClosedContour ──────────────────────────

  /**
   * Computes the perimeter of the convex hull of this polygon
   *
   * All distances respect anisotropic pixel spacing.
   * Returned coordinates are in internal sub-pixel space.
   * Returned hullPerimeter is in mm.
   *
   * @param debug       If true, opens a popup window with a visual overlay
   */
  public getConvexHullSplineLength(debug: boolean = false): {
    hullPoints: PointArray;
    hullPerimeter: number;
  } {
    const spacing = this.getPixelSpacing();
    const sx = spacing[1] / ContourBase.subMatrix;
    const sy = spacing[0] / ContourBase.subMatrix;

    // remove closing duplicate
    const polygon
      = this._polygon.length > 0
        && this._polygon[0].x === this._polygon[this._polygon.length - 1].x
        && this._polygon[0].y === this._polygon[this._polygon.length - 1].y
        ? this._polygon.slice(0, -1)
        : [...this._polygon];

    const hull = AreaContourBase.convexHull(polygon);

    if (hull.length < 3) {
      return {
        hullPoints: hull,
        hullPerimeter: 0,
      };
    }

    // hull perimeter in mm
    let hullPerimeter = 0;
    for (let i = 0; i < hull.length; i++) {
      hullPerimeter += AreaContourBase.anisotropicDist(
        hull[i],
        hull[(i + 1) % hull.length],
        sx,
        sy,
      );
    }

    if (debug) {
      AreaContourBase.debugDrawConvexHullSpline(polygon, hull, hullPerimeter);
    }

    return { hullPoints: hull, hullPerimeter };
  }
}
